import {ReactElement, useCallback, useMemo, useState} from 'react';
import {
  CartCheckoutButton,
  CartCost,
  CartLines,
  Link,
  useCart,
} from '@shopify/hydrogen';

import CartLineItem from '../cart/CartLineItem.client';
import ReactModal from 'react-modal';
import {useCartUI} from '../../hooks';
import VectorIcon from '../VectorIcon';
import {useAuthService} from '../../contexts/AuthServiceProvider.client';
import {calculateRewardsCartInfo} from '../../utils/rewards';

export default function CartModal(): ReactElement {
  const {isCartOpen, closeCart} = useCartUI();
  const {discountCodes, discountCodesUpdate} = useCart();
  const [discountCodeInputValue, setDiscountCodeInputValue] = useState('');
  const {authUser} = useAuthService();

  const discountCodeInputChangeHandler = useCallback(
    (event) => setDiscountCodeInputValue(event.target.value),
    [],
  );

  const applyDiscountButtonClickHandler = useCallback(() => {
    discountCodesUpdate([discountCodeInputValue]);
  }, [discountCodeInputValue, discountCodesUpdate]);

  const discountCode = useMemo(
    () => (discountCodes?.length > 0 ? discountCodes[0].code : null),
    [discountCodes],
  );

  const removeDiscounts = useCallback(
    () => discountCodesUpdate([]),
    [discountCodesUpdate],
  );

  const {lines, cost} = useCart();

  const pointsInfo = useMemo(() => {
    const totalAmount = cost?.totalAmount?.amount;
    if (authUser?.member?.is_rewards_member || totalAmount == null) {
      return null;
    }

    const {points, discount} = calculateRewardsCartInfo(lines, totalAmount);

    if (points > 0) {
      if (discount > 0) {
        const discountVal = discount.toFixed(2);
        return `Earn ${points}+ points and save $${discountVal} on this order.`;
      }
      return `Earn ${points}+ points on this order.`;
    }

    return null;
  }, [authUser?.member?.is_rewards_member, cost?.totalAmount?.amount, lines]);

  return (
    <ReactModal
      isOpen={isCartOpen}
      className="cart-preview"
      overlayClassName="cart-preview-outer modal_active"
      shouldCloseOnOverlayClick={true}
      onRequestClose={closeCart}
      closeTimeoutMS={500}
    >
      <div className="cart-preview__header">
        <h6 className="cart-preview__title title-2">Your Cart</h6>
        <div
          role="button"
          tabIndex={-1}
          onClick={closeCart}
          className="cart-preview__close"
          id="cart-preview-close"
          title="Close"
        >
          <div className="close-custom" />
        </div>
      </div>
      <div className="cart-preview__content">
        <div className="cart-preview__list">
          <CartLines>
            <CartLineItem />
          </CartLines>
        </div>
      </div>
      <div className="cart-preview__footer">
        {pointsInfo && (
          <div className="cart-preview__points">
            {pointsInfo} <Link to="/l/rewards">Find out how</Link>
          </div>
        )}
        <div className="cart-preview__summary">
          <span>SUBTOTAL</span>
          <strong className="price-subtotal">
            $<CartCost amountType="subtotal" as="span" withoutCurrency />
          </strong>
        </div>
        <div className="cart-preview__discount mt-10">
          {discountCode ? (
            <>
              <span>DISCOUNT(S)</span>
              <div className="cart-preview__discount-code">
                <button
                  className="cart-preview__discount-code-remove"
                  onClick={removeDiscounts}
                >
                  <VectorIcon iconName="trash" />
                </button>
                <strong className="price-subtotal">{discountCode}</strong>
              </div>
            </>
          ) : (
            <>
              <input
                className="cart-preview__discount-code-input form__element"
                value={discountCodeInputValue}
                onChange={discountCodeInputChangeHandler}
                placeholder="Discount code"
              />
              <button onClick={applyDiscountButtonClickHandler}>
                Apply Discount
              </button>
            </>
          )}
        </div>
        <div className="cart-preview__summary">
          <span>TOTAL</span>
          <strong className="price-subtotal">
            $<CartCost amountType="total" as="span" withoutCurrency />
          </strong>
        </div>
        <div className="cart-preview__footer-info">
          Tax included. Shipping calculated at checkout.
        </div>
        <CartCheckoutButton className="button button_full button_white_hover mt-20">
          Checkout
        </CartCheckoutButton>
      </div>
    </ReactModal>
  );
}
